import type { Accessorial } from "~/shared/schemas/accessorial-schema.ts";
import type { ShipmentManagementPickupDeliveryDetails } from "~/shared/schemas/shipment-management-schema.ts";
import type { ApplyableAddress } from "~/shared/types/applyable-address-types.ts";
import type { StopContact, StopName } from "~/shared/types/stop-types.ts";
import {
	formatTimeToString,
	parseTimeString,
} from "~/shared/utils/time-utils.ts";

import { parseAccessorialIds } from "./parse-accessorial-ids.ts";

type BuildStopFromAddressOptions = {
	address: ApplyableAddress;
	name: StopName;
	stop: ShipmentManagementPickupDeliveryDetails;
	locationTypeOptions: Accessorial[];
	accessorialOptions: Accessorial[];
};

/**
 * Maps a selected saved address onto stop and contact patches. The address book
 * item itself is never updated from here -- the result is applied to the draft.
 */
export const buildStopFromAddress = ({
	address,
	name,
	stop,
	locationTypeOptions,
	accessorialOptions,
}: BuildStopFromAddressOptions): {
	stop: Partial<ShipmentManagementPickupDeliveryDetails>;
	contact: Partial<StopContact>;
} => {
	const stopType = name === "pickup" ? 1 : 2;
	const detail =
		address.details?.find((item) => item.stopType === stopType) ??
		address.details?.[0];
	const addressLine = [address.address1, address.address2]
		.filter((part) => part && part.trim().length > 0)
		.join(", ");
	const stateZip = [address.stateProvince, address.postalCode]
		.filter((part) => part && part.trim().length > 0)
		.join(" ");
	// Match the format returned by the get-shipment endpoint so the optimistic
	// update matches a later refetch (and stays parseable by
	// parseCombinedAddress): "address1, City, ST zip, Country".
	const companyAddress = [addressLine, address.city, stateZip, address.country]
		.filter((part) => part && part.trim().length > 0)
		.join(", ");

	// Map the saved accessorial IDs back onto the stop's location type name and
	// selectable accessorial names.
	const detailAccessorialIds = parseAccessorialIds(detail?.accessorials);
	const selectedLocationType =
		locationTypeOptions.find((option) =>
			detailAccessorialIds.includes(option.id),
		) ?? locationTypeOptions.find((option) => option.name === "Commercial");
	const selectedAccessorials = accessorialOptions
		.filter((option) => detailAccessorialIds.includes(option.id))
		.map((option) => option.name);

	// Selecting an address book item replaces the entire section -- any field
	// left blank on the saved address should clear the stop's prior value
	// rather than leaving it in place.
	return {
		stop: {
			id: stop.id,
			companyName: address.companyName,
			address: companyAddress,
			address1: address.address1,
			address2: address.address2,
			city: address.city,
			stateProvince: address.stateProvince,
			country: address.country,
			postalCode: address.postalCode,
			locationType: selectedLocationType?.name,
			accessorials: selectedAccessorials,
			specialInstructions: detail?.specialInstructions,
			shipmentHoursFrom: formatTimeToString(parseTimeString(detail?.hoursOpen)),
			shipmentHoursTo: formatTimeToString(parseTimeString(detail?.hoursClose)),
		},
		contact: {
			name: detail?.contactName,
			phone: detail?.contactPhone,
			extension: detail?.contactExtension,
		},
	};
};
